/**
 * Argus's tool handlers and its snapshot resource — what the host calls over the direct bridge.
 * Each tool's schema, title and policy live in `contract.ts` (build time only: build.mjs generates the
 * `.well-known` manifest from it, and the host validates every call against that manifest). This file
 * takes only the contract's TYPES, so zod never reaches the bundle; a handler the manifest does not
 * list, or a tool with no handler, fails the type check (`HANDLERS` is keyed by the contract's names).
 *
 * Every tool is a thin wrapper over `frontend/actions.js` (read through `window.__argusActions`),
 * the same functions the operator's controls call, so manual use and Dobby can never diverge.
 */

import type { McpResult, ResourceHandler, ToolContext } from "@talkie/agentic-app-transport";
import type { ToolArgs, ToolName } from "./contract";

// ─────────────────────────── Shared actions (frontend/actions.js) ──

interface Snapshot { id: string; thumb_data_url: string }
interface ArgusActions {
  focusCamera(id: string): object;
  restoreGrid(): object;
  rewind(id: string, seconds?: number): Promise<object>;
  goLive(id?: string): object;
  unmuteCamera(id: string): object;
  muteAll(): object;
  setOverlay(id: string, kind: string, enabled?: boolean): object;
  snapshot(id: string): Promise<Record<string, unknown>>;
  snapshotById(id: string): Snapshot | null;
  getSnapshot(id?: string): Record<string, unknown>;
  SNAPSHOT_AUTHORITY: string;
  acknowledgeAlert(id: string, disposition?: string): object;
  activeAlerts(): object;
  listCameras(): object;
  cameraStatus(id: string): object;
}

declare global {
  interface Window {
    __argusActions?: ArgusActions;
  }
}

function actions(): ArgusActions {
  if (!window.__argusActions) throw new Error("argus bridge: actions.js must load before bridge.bundle.js");
  return window.__argusActions;
}

/**
 * A tool result: the payload as `structuredContent` (what a host and Dobby's planner read as data),
 * and the same JSON as text for clients that only show text. Without the structured half an id
 * Argus returns — a `snapshot_id` from `list_cameras` — is only observation PROSE to the planner,
 * which (rightly, against injection) refuses to chain a reference off it.
 */
function json(payload: object): McpResult {
  return { structuredContent: payload, content: [{ type: "text", text: JSON.stringify(payload) }] };
}

/** A snapshot result: its metadata, and the image as a resource the host can materialize and attach. */
function snapshotResult(result: Record<string, unknown>): McpResult {
  return {
    structuredContent: result,
    content: [
      { type: "text", text: String(result.view) },
      { type: "resource_link", uri: String(result.snapshot_uri), mimeType: "image/jpeg", name: String(result.filename), title: String(result.caption_hint) },
    ],
  };
}

type Handler<K extends ToolName> = (args: ToolArgs<K>, ctx: ToolContext) => McpResult | Promise<McpResult>;

/** One handler per manifest tool. A failure `actions.js` throws becomes the call's `isError` result. */
export const HANDLERS: { readonly [K in ToolName]: Handler<K> } = {
  list_cameras: () => json(actions().listCameras()),
  get_camera_status: ({ camera_id }) => json(actions().cameraStatus(camera_id)),
  focus_camera: ({ camera_id }) => json(actions().focusCamera(camera_id)),
  restore_grid: () => json(actions().restoreGrid()),
  rewind: async ({ camera_id, seconds }) => json(await actions().rewind(camera_id, seconds)),
  go_live: ({ camera_id }) => json(actions().goLive(camera_id)),
  mute_all: () => json(actions().muteAll()),
  unmute_camera: ({ camera_id }) => json(actions().unmuteCamera(camera_id)),
  snapshot: async ({ camera_id }) => snapshotResult(await actions().snapshot(camera_id)),
  get_snapshot: ({ snapshot_id }) => snapshotResult(actions().getSnapshot(snapshot_id)),
  get_active_alerts: () => json(actions().activeAlerts()),
  acknowledge_alert: ({ alert_id, disposition }) => json(actions().acknowledgeAlert(alert_id, disposition)),
  toggle_analytics_overlay: ({ camera_id, kind, enabled }) => json(actions().setOverlay(camera_id, kind, enabled ?? true)),
};

/** The snapshot store, read by the host to attach a frame a tool returned. */
export const SNAPSHOT_RESOURCE = "argus://{authority}/snapshots/{id}";

export const readSnapshot: ResourceHandler = (uri, { variables }) => {
  if (variables.authority !== actions().SNAPSHOT_AUTHORITY) throw new Error(`argus authority "${variables.authority}" is not owned by this instance`);
  const entry = actions().snapshotById(variables.id);
  if (!entry) throw new Error(`argus snapshot "${variables.id}" is not in the 3-deep store (three newer snapshots replaced it, or it was never captured)`);
  const comma = entry.thumb_data_url.indexOf(",");
  const mimeType = /^data:([^;]+)/.exec(entry.thumb_data_url)?.[1] ?? "image/jpeg";
  return { contents: [{ uri, mimeType, blob: entry.thumb_data_url.slice(comma + 1) }] };
};
